import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { getChartPalette, chartStateShell } from "./chartTheme";

type LineChartProps = {
  title: string;
  xLabel: string;
  yLabel: string;
  data: any;
  isFetching?: boolean;
  isDarkMode?: boolean;
};

const LineChart = ({
  title,
  xLabel,
  yLabel,
  data,
  isFetching,
  isDarkMode = true,
}: LineChartProps) => {
  const getDisplaySeriesName = (seriesName: string | undefined) => {
    const normalized = String(seriesName ?? "").trim().toLowerCase();
    return normalized === "y" ? "total" : seriesName || "Series";
  };

  const formattedData = useMemo(
    () => (typeof data === "object" && data !== null ? data : {}),
    [data]
  );

  // Check if response is successful but categories array is empty
  const isNoDataFound = useMemo(() => {
    const entryExits = formattedData?.Entry_Exits || {};
    const categories = entryExits.categories || [];
    const series = entryExits.series || [];
    
    // If categories array length is 0 or less OR series array length is 0 or less, show "No data found"
    return categories.length <= 0 || series.length <= 0;
  }, [formattedData]);

  const option = useMemo(() => {
    const c = getChartPalette(isDarkMode);
    const hasValidDataForOption = formattedData && formattedData.Entry_Exits && formattedData.Entry_Exits.categories && formattedData.Entry_Exits.series;

    let categories = hasValidDataForOption ? formattedData.Entry_Exits.categories : [];
    categories = categories.map((cat: string) => {
      if (cat.endsWith('UTC')) {
        return cat.slice(0, -4).trim();
      }
      return cat;
    });

    const legendData = hasValidDataForOption
      ? formattedData.Entry_Exits.series.map((s: any) => getDisplaySeriesName(s.name))
      : [];

    const seriesData = hasValidDataForOption ? formattedData.Entry_Exits.series.map((s: any) => ({
      name: getDisplaySeriesName(s.name),
      type: 'line',
      smooth: true,
      data: s.data
    })) : [];

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' }
      },
      legend: {
        data: legendData,
        top: 30,
        right: 10,
        textStyle: { color: c.secondaryTextColor }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '15%',
        containLabel: true
      },
      toolbox: {
        feature: {
          saveAsImage: { title: 'Save', name: title }
        },
        iconStyle: { borderColor: c.borderColor }
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: {
          color: c.secondaryTextColor,
          fontSize: 12
        },
        axisLine: { lineStyle: { color: c.axisLineColor } }
      },
      yAxis: {
        type: 'value',
        name: yLabel,
        nameTextStyle: { color: c.secondaryTextColor, fontSize: 12 },
        axisLabel: { color: c.secondaryTextColor, fontSize: 12 },
        axisLine: { lineStyle: { color: c.axisLineColor } },
        splitLine: { lineStyle: { color: c.splitLineColor, opacity: 0.5 } }
      },
      dataZoom: [
        {
          type: 'slider',
          xAxisIndex: 0,
          start: 90,
          end: 100,
          height: 12,
          bottom: 10,
          showDetail: false,
          dataBackground: {
            lineStyle: { color: 'transparent' },
            areaStyle: { color: 'transparent' }
          },
          textStyle: { color: c.secondaryTextColor },
        },
      ],
      series: seriesData,
      color: c.colors,
    };
  }, [title, yLabel, formattedData, isDarkMode]);

  // Show loading message when request is going to backend/response not coming yet
  if (isFetching) {
    return (
      <div className={chartStateShell}>
        <div className="px-4 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-300 border-t-blue-500 dark:border-slate-500" />
          </div>
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
            Loading chart data...
          </p>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            We’re fetching the latest trends for this graph.
          </p>
        </div>
      </div>
    );
  }

  // Show "no data" message when response comes successfully but categories/series are empty
  if (isNoDataFound) {
    return (
      <div className={chartStateShell}>
        <div className="px-4 text-center">
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
            No data found for this chart.
          </p>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            Modify your filters to view entry/exit activity here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '58vh', width: '100%' }}>
    <ReactECharts
      option={option}
      style={{ height: '98%', width: "100%" }}
      opts={{ renderer: 'canvas' }}
      className="chart-container no-hover"
    />
    </div>
  );
};

export default LineChart;

